import type { GLToastProps } from '@group-link-one/gl-components';
import type { ISPPageTitle } from '@group-link-one/gl-ui/sonarprint';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

type PageLayoutContextType = {
  content: ReactElement | null;
  snackbar: GLToastProps | null;
  footerText?: string;
  /** Title rendered in the global topbar's left side. `null` keeps it empty (default). */
  pageTitle: ISPPageTitle | null;
  setSnackbarState: (params: GLToastProps | null) => void;
  setContentState: (newContent: ReactElement | null) => void;
  setPageTitle: (title: ISPPageTitle | null) => void;
};

const PageLayoutContext = createContext<PageLayoutContextType | undefined>(undefined);

type PageLayoutProviderProps = {
  children: ReactNode;
  footerText?: string;
};

export const PageLayoutProvider = ({ footerText, children }: PageLayoutProviderProps) => {
  const [content, setContent] = useState<ReactElement | null>(null);
  const [snackbar, setSnackbar] = useState<GLToastProps | null>(null);
  const [pageTitle, setPageTitleState] = useState<ISPPageTitle | null>(null);

  function setContentState(newContent: ReactElement | null) {
    setContent(newContent);
  }

  function setSnackbarState(params: GLToastProps | null) {
    setSnackbar(params);
  }

  // Stable identity so consumers can safely use it as an effect dependency.
  const setPageTitle = useCallback((title: ISPPageTitle | null) => {
    setPageTitleState(title);
  }, []);

  return (
    <PageLayoutContext.Provider
      value={{
        content,
        snackbar,
        footerText,
        pageTitle,
        setContentState,
        setSnackbarState,
        setPageTitle,
      }}
    >
      {children}
    </PageLayoutContext.Provider>
  );
};

export const usePageLayout = (): PageLayoutContextType => {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within a PageLayoutProvider');
  }
  return context;
};
